import moment from "moment";

type Reservation = {
  id: number;
  isBorrowing: boolean;
  from: string;          // "YYYY-MM-DD"
  to: string;            // "YYYY-MM-DD"
  from_time?: string;     // "HH:MM:SS" (optional)
  to_time?: string;       // "HH:MM:SS" (optional)
  recurring?: RecurringRecord[];
  modifications?: ModificationRecord[];
  comment: string;       // vehicle comment (top-level)
  user: any;
};

type RecurringRecord = {
  id: number;
  comment?: string;
  start_date?: string;    // "YYYY-MM-DD" (required for recurring, but we'll fallback)
  end_date?: string | null;
  is_cancelled?: boolean;
  recurring_frequency: "daily" | "weekly" | "monthly" | "yearly" | string;
  recurring_gap?: number;          // default 1
  days_of_week?: number[];         // for weekly: 0..7 (0 and 7 = Sunday)
  dates?: number[];
  week_numbers?: number[];
  recurring_months?: number[];
  start_time?: string;  // "HH:MM:SS"
  end_time?: string;    // "HH:MM:SS"
};

type ModificationRecord = {
  borrowings_id: number;
  comment: string;
  construction_id: number;
  id: number;
  is_cancelled: boolean;
  old_start: string;  // "YYYY-MM-DD HH:MM:SS"
  new_start: string;  // "YYYY-MM-DD HH:MM:SS"
  new_end: string;    // "YYYY-MM-DD HH:MM:SS"
};

type VehicleData = {
  id: number;
  title: string;
  history?: Reservation[];
};

type VehicleReservationEvent = {
  parentId: number | null;
  date: string;                  // "YYYY-MM-DD"
  endDate?: string;              // "YYYY-MM-DD" only for non-recurring, multi-day reservations
  fromTime?: string;             // "HH:MM:SS"
  toTime?: string;               // "HH:MM:SS"
  title: string;                 // vehicle title (top-level)
  comment: string;               // vehicle comment (top-level)
  vehicleId: number;             // top-level id
  isReturned: boolean;
  isLate: boolean;
  isRecurring: boolean;
  borrowerName: string;
  isCancelled?: boolean;                    // from modification.is_cancelled
  modificationId?: number;                  // the modification record id
  modificationConstructionId?: number|null; // modification.construction_id
  modificationComment?: string|null;        // modification.comment
};

/**
 * Calculate all reservations for a vehicle that occur within a given month,
 * plus a 7-day buffer before and after the month.
 *
 * Rules implemented:
 * - Only history items with isBorrowing=true are considered.
 * - If history.recurring is empty/undefined => one-time reservation using history.from/to (inclusive).
 *   Emits one ReservationOut per day in [from, to]. Includes from_time/to_time if present on the history item.
 * - If history.recurring is non-empty => ignore history.from/to.
 *   Multiple recurring records can overlap: the one with the later start_date supersedes earlier ones
 *   starting from its start_date; earlier records stop the day before any later record's start_date.
 *   Each record also stops at its own end_date (if present).
 *   Supported recurring_frequency: "daily" and "weekly".
 *   - daily: occurrences on start_date, then every `recurring_gap` days.
 *   - weekly: use days_of_week (0..7 where 0 and 7 are Sunday) and `recurring_gap` (weeks).
 *             Week boundaries are Sunday-based. In the first (partial) week, only days on/after
 *             the start_date are considered (earlier weekdays are shifted to the following week).
 *   "monthly" and "yearly" are ignored (contribute no events for now).
 *
 * @param month - string "YYYY-MM"
 * @param data  - full vehicle data object containing top-level id/title and `history` array
 */
export function calculateReservationsInMonth(month: string, data: VehicleData): VehicleReservationEvent[] {
  function numbersToDate(y:number,m:number,d:number) {
    return new Date(y, m-1, d); // local date, JST is fine
  }

  function dateToString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth()+1).padStart(2,"0");
    const d = String(date.getDate()).padStart(2,"0");
    return `${y}-${m}-${d}`;
  }

  function stringToDate(s: string): Date {
    const [y,m,d] = s.split("-").map(Number);
    return numbersToDate(y,m,d);
  }

  function stringToNumbers(s: string): { y: number; m: number; d: number } {
    const [y,m,d] = s.split("-").map(Number);
    return { y, m, d };
  }

  const toNumberArray = (value: any): number[] => {
    if (Array.isArray(value)) return value.map(Number);

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.map(Number) : [];
      } catch {
        return [];
      }
    }

    return [];
  };

  function addDays(s: string, days: number): string {
    const dt = stringToDate(s);
    dt.setDate(dt.getDate() + days);
    return dateToString(dt);
  }

  function compareDate(a: string, b: string): number {
    return a.localeCompare(b);  // works because YYYY-MM-DD format sorts naturally
  }
  const minDate = (a: string, b: string) => (compareDate(a, b) <= 0 ? a : b);
  const maxDate = (a: string, b: string) => (compareDate(a, b) >= 0 ? a : b);

  const startOfMonth = `${month}-01`;
  const endOfMonth = (() => {
    const { y, m } = stringToNumbers(startOfMonth);
    // first day of *this* month
    const first = numbersToDate(y, m, 1);
    // last day: day 0 of next month
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    return dateToString(last);
  })();

  // Buffer window: 14 days before month start, 14 days after month end (inclusive)
  const windowStart = addDays(startOfMonth, -14);
  const windowEnd = addDays(endOfMonth, +14);

  const results: VehicleReservationEvent[] = [];
  const vehicleId = data.id;
  const title = data.title ?? "";

  // Actually, we need returned records too, to show as gray
  //const relevantHistory = (data.history ?? []).filter(h => h && h.isBorrowing);
  const relevantHistory = data.history ?? [];

  // Normalize Sunday values (0 and 7 => 0)
  const normalizeDowList = (input?: number[] | string): number[] => {
    // If the input is a string, try to parse it into an array
    if (typeof input === "string") {
      try {
        input = JSON.parse(input);
      } catch {
        return []; // invalid JSON → return empty
      }
    }

    // Ensure we have an array
    if (!Array.isArray(input) || input.length === 0) return [];

    const uniq = new Set<number>();

    for (const n of input) {
      if (n === 0 || n === 7) uniq.add(0);
      else if (n >= 1 && n <= 6) uniq.add(n);
    }

    return [...uniq].sort((a, b) => a - b);
  };
  // Clamp [a,b] to [ws,we], return null if disjoint
  const clampRange = (a: string, b: string, ws: string, we: string): { from: string; to: string } | null => {
    const from = maxDate(a, ws);
    const to = minDate(b, we);
    return compareDate(from, to) <= 0 ? { from, to } : null;
  };

  // Returns "YYYY-MM-DD HH:MM:SS" or "" if unbounded / no occurrences.
  // This is very repetitive (WET) but works.
  function lastOccurrenceString(history: {
    id: number;
    isBorrowing: boolean;
    from: string;         // "YYYY-MM-DD"
    to: string;           // "YYYY-MM-DD"
    from_time?: string;   // "HH:MM:SS"
    to_time?: string;     // "HH:MM:SS"
    recurring?: Array<{
      id: number;
      comment?: string;
      start_date?: string;             // "YYYY-MM-DD"
      end_date?: string | null;
      is_cancelled?: boolean;
      recurring_frequency: "daily" | "weekly" | "monthly" | "yearly" | string;
      recurring_gap?: number;          // >=1
      days_of_week?: number[];         // 0..7 (0/7 = Sunday)
      dates?: number[];                // for monthly date-of-month
      week_numbers?: number[];         // monthly/yearly nth-week mode: 1..4 or -1
      recurring_months?: number[];     // 1..12
      start_time?: string;             // "HH:MM:SS"
      end_time?: string;               // "HH:MM:SS"
    }>;
    modifications?: Array<{
      borrowings_id: number;
      comment: string;
      construction_id: number;
      id: number;
      is_cancelled: boolean;
      old_start: string;  // "YYYY-MM-DD HH:MM:SS"
      new_start: string;  // "YYYY-MM-DD HH:MM:SS"
      new_end: string;    // "YYYY-MM-DD HH:MM:SS"
    }>;
    comment: string;
    user: any;
  }): string {
    // ----- Small date utils (YYYY-MM-DD only) -----
    const toDate = (s: string) => {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d);
    };
    const toYMD = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    const addDays = (s: string, n: number) => {
      const d = toDate(s);
      d.setDate(d.getDate() + n);
      return toYMD(d);
    };
    const cmpDate = (a: string, b: string) => a.localeCompare(b);
    const lastOfMonth = (ymd: string) => {
      const d = toDate(ymd);
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return toYMD(last);
    };
    const buildYMDIfValid = (y: number, m: number, day: number) => {
      const dt = new Date(y, m - 1, day);
      if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== day) return null;
      return toYMD(dt);
    };
    const parseDateStrict = (maybe: string | undefined | null, h: typeof history) => {
      const v = maybe ?? h.from;
      if (!v) return null;
      try {
        // validate
        return toYMD(toDate(v));
      } catch {
        return null;
      }
    };
    const normDOW = (a?: number[] | string) => {
      if (typeof a === "string") {
        try {
          a = JSON.parse(a);
        } catch {
          return []; // invalid string → empty
        }
      }

      if (!Array.isArray(a) || a.length === 0) return [];

      const s = new Set<number>();
      for (const n of a) {
        if (n === 0 || n === 7) s.add(0);
        else if (n >= 1 && n <= 6) s.add(n);
      }

      return Array.from(s).sort((x, y) => x - y);
    };
    const monthsSet = (a?: number[]) => {
      if (!a || a.length === 0) return null; // meaning "all months"
      const s = new Set<number>();
      for (const m of a) if (m >= 1 && m <= 12) s.add(m);
      return s;
    };

    // ----- Supersession windows (mirrors CalculateRecurringBorrowings.ts) -----
    type Window = { record: any; from: string; to: string | null };

    const buildActiveWindows = (recs: NonNullable<typeof history.recurring>) => {
      const withStarts = recs
        .filter(r => r && !r.is_cancelled)
        .map(r => ({
          ...r,
          _start: parseDateStrict(r.start_date, history),
          _end: r.end_date ? r.end_date : null,
        }))
        .filter(r => r._start) as Array<typeof recs[number] & { _start: string; _end: string | null }>;

      withStarts.sort((a, b) => cmpDate(a._start, b._start));
      const out: Window[] = [];
      for (let i = 0; i < withStarts.length; i++) {
        const cur = withStarts[i];
        const next = withStarts[i + 1];
        let to: string | null;
        if (next) {
          const dayBeforeNext = addDays(next._start, -1);
          to = cur._end ? (cmpDate(cur._end, dayBeforeNext) <= 0 ? cur._end : dayBeforeNext) : dayBeforeNext;
        } else {
          to = cur._end ?? null; // unbounded
        }
        out.push({ record: cur, from: cur._start, to });
      }
      return out;
    };

    // ----- Previous occurrence helpers (for cancellation of the last one) -----
    // Return last date <= limit that matches the pattern inside [win.from, win.to]
    const lastDateDaily = (win: Window, limit: string) => {
      if (!win.to) return null; // unbounded
      const gap = Math.max(1, win.record.recurring_gap ?? 1);
      const start = win.from;
      const d0 = toDate(start).getTime();
      const d1 = toDate(limit).getTime();
      if (d1 < d0) return null;
      const msPerDay = 86400000;
      const diff = Math.floor((d1 - d0) / msPerDay);
      const steps = Math.floor(diff / gap);
      const cand = addDays(start, steps * gap);
      return (cmpDate(cand, win.from) >= 0 && (!win.to || cmpDate(cand, win.to) <= 0)) ? cand : null;
    };

    const lastDateWeekly = (win: Window, limit: string) => {
      if (!win.to) return null;
      const days = normDOW(win.record.days_of_week);
      if (days.length === 0) return null;
      const gapWeeks = Math.max(1, win.record.recurring_gap ?? 1);
      const stepDays = gapWeeks * 7;

      const s = toDate(win.from);
      const sDow = s.getDay(); // 0..6
      let best: string | null = null;

      for (const dow of days) {
        const offset = dow >= sDow ? dow - sDow : 7 - (sDow - dow);
        let first = addDays(win.from, offset);
        if (cmpDate(first, win.from) < 0) first = win.from;

        // find last occurrence <= limit by stepping stepDays
        if (cmpDate(limit, first) < 0) continue;
        const msPerDay = 86400000;
        const diffDays = Math.floor((toDate(limit).getTime() - toDate(first).getTime()) / msPerDay);
        const steps = Math.floor(diffDays / stepDays);
        const cand = addDays(first, steps * stepDays);
        if (cmpDate(cand, win.from) >= 0 && (!win.to || cmpDate(cand, win.to) <= 0)) {
          if (!best || cmpDate(cand, best) > 0) best = cand;
        }
      }
      return best;
    };

    const lastDateMonthlyDateOfMonth = (win: Window, limit: string) => {
      if (!win.to) return null;
      const allowed = monthsSet(win.record.recurring_months);
    const days: number[] = (win.record.dates && win.record.dates.length > 0)
      ? [...new Set<number>(toNumberArray(win.record.dates).filter((d): d is number => d >= 1 && d <= 31))]
          .sort((a: number, b: number) => a - b)
      : [Number(parseDateStrict(win.record.start_date, history)!.split("-")[2])]; // default to start_date day
      if (days.length === 0) return null;

      let y = toDate(limit).getFullYear();
      let m = toDate(limit).getMonth() + 1;

// eslint-disable-next-line no-constant-condition
      while (true) {
        if (allowed && !allowed.has(m)) {
          // go to previous month
          m -= 1; if (m === 0) { m = 12; y -= 1; }
          if (cmpDate(`${y}-01-01`, win.from) < 0) return null;
          continue;
        }
        // try largest day that fits this month and <= limit
        for (let i = days.length - 1; i >= 0; i--) {
          const d = days[i];
          const cand = buildYMDIfValid(y, m, d);
          if (!cand) continue;
          if (cmpDate(cand, limit) > 0) continue;
          if (cmpDate(cand, win.from) < 0) { /* try earlier month */ break; }
          if (!win.to || cmpDate(cand, win.to) <= 0) return cand;
        }
        // previous month
        m -= 1; if (m === 0) { m = 12; y -= 1; }
        if (cmpDate(`${y}-01-01`, win.from) < 0) return null;
      }
    };

    const nthWeekdayOfMonth = (y: number, m: number, dow: number, nth: number) => {
      // nth: 1..4, or -1 for last
      const first = buildYMDIfValid(y, m, 1)!;
      const firstDow = toDate(first).getDay();
      let day1 = 1 + ((dow - firstDow + 7) % 7); // first occurrence of dow in month
      if (nth === -1) {
        // last occurrence: start from end of month and step back
        const last = lastOfMonth(first);
        let d = Number(last.split("-")[2]);
        // find last 'dow' <= last
        while (toDate(buildYMDIfValid(y, m, d)!).getDay() !== dow) d--;
        return buildYMDIfValid(y, m, d);
      } else {
        const day = day1 + (nth - 1) * 7;
        return buildYMDIfValid(y, m, day);
      }
    };

    const lastDateMonthlyNthWeek = (win: Window, limit: string) => {
      if (!win.to) return null;
      const allowed = monthsSet(win.record.recurring_months);
      const dows = normDOW(win.record.days_of_week);
      const weeks = toNumberArray(win.record.week_numbers).filter(n => n === -1 || (n >= 1 && n <= 4));
      if (dows.length === 0 || weeks.length === 0) return null;

      let y = toDate(limit).getFullYear();
      let m = toDate(limit).getMonth() + 1;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (allowed && !allowed.has(m)) {
          m -= 1; if (m === 0) { m = 12; y -= 1; }
          if (cmpDate(`${y}-01-01`, win.from) < 0) return null;
          continue;
        }
        // produce all candidates this month, pick the latest <= limit
        let best: string | null = null;
        for (const w of weeks) {
          for (const dow of dows) {
            const cand = nthWeekdayOfMonth(y, m, dow, w);
            if (!cand) continue;
            if (cmpDate(cand, limit) > 0) continue;
            if (cmpDate(cand, win.from) < 0) continue;
            if (win.to && cmpDate(cand, win.to) > 0) continue;
            if (!best || cmpDate(cand, best) > 0) best = cand;
          }
        }
        if (best) return best;
        m -= 1; if (m === 0) { m = 12; y -= 1; }
        if (cmpDate(`${y}-01-01`, win.from) < 0) return null;
      }
    };

    const lastDateYearly = (win: Window, limit: string) => {
      if (!win.to) return null;
      const gapYears = Math.max(1, win.record.recurring_gap ?? 1);
      const dows = normDOW(win.record.days_of_week);
      const weeks = toNumberArray(win.record.week_numbers).filter(n => n === -1 || (n >= 1 && n <= 4));
      const allowed = monthsSet(win.record.recurring_months);

      const start = toDate(win.from);
      const startYear = start.getFullYear();
      const limitYear = toDate(limit).getFullYear();

      for (let y = limitYear; y >= startYear; y--) {
        // align with gapYears (only years y where (y - startYear) % gapYears === 0)
        if (((y - startYear) % gapYears) !== 0) continue;

        // Two modes: (a) date-of-month using start_date's month/day, (b) nth weekday in allowed months
        if (!weeks || weeks.length === 0 || dows.length === 0) {
          const [sm, sd] = parseDateStrict(win.record.start_date, history)!.split("-").slice(1).map(Number);
          if (allowed && !allowed.has(sm)) continue;
          const cand = buildYMDIfValid(y, sm, sd);
          if (!cand) continue;
          if (cmpDate(cand, limit) > 0) continue;
          if (cmpDate(cand, win.from) < 0) continue;
          if (win.to && cmpDate(cand, win.to) > 0) continue;
          return cand;
        } else {
          // nth-weekday mode across allowed months (or all months if none specified)
          let best: string | null = null;
          for (let m = 1; m <= 12; m++) {
            if (allowed && !allowed.has(m)) continue;
            for (const w of weeks) {
              for (const dow of dows) {
                const cand = nthWeekdayOfMonth(y, m, dow, w);
                if (!cand) continue;
                if (cmpDate(cand, limit) > 0) continue;
                if (cmpDate(cand, win.from) < 0) continue;
                if (win.to && cmpDate(cand, win.to) > 0) continue;
                if (!best || cmpDate(cand, best) > 0) best = cand;
              }
            }
          }
          if (best) return best;
        }
      }
      return null;
    };

    // ----- Compute base last occurrence (date + end_time) -----
    const recs = history.recurring ?? [];
    let baseLastStart: string | null = null; // "YYYY-MM-DD HH:MM:SS" (start)
    let baseLastEnd: string | null = null;   // "YYYY-MM-DD HH:MM:SS" (end)

    if (!recs || recs.length === 0) {
      // One-time (possibly multi-day). End at history.to + to_time (or 23:59:59 if missing).
      //console.log(`One-time reservation for history id=${history.id}`);
      //console.log(history);
      const lastDate = history.to || history.from;
      if (lastDate) {
        const endTime = history.to_time || "23:59:59";
        baseLastEnd = `${lastDate} ${endTime}`;
        const startTime = history.from_time || "00:00:00";
        baseLastStart = `${lastDate} ${startTime}`; // same-day last occurrence start (best effort)
      }
    } else {
      const windows = buildActiveWindows(recs as any);
      // find the truly last occurrence across windows
      let bestDate: string | null = null;
      let bestRecord: any = null;

      for (const w of windows) {
        if (!w.to) {
          // unbounded window => the recurrence never ends; we can only resolve by modifications later
          bestDate = null; // keep unbounded flag
          bestRecord = w.record;
          continue;
        }

        const limit = w.to; // last day we may have an occurrence in this window
        let lastDate: string | null = null;
        const freq = (w.record.recurring_frequency || "").toLowerCase();

        if (freq === "daily") {
          lastDate = lastDateDaily(w, limit);
        } else if (freq === "weekly") {
          lastDate = lastDateWeekly(w, limit);
        } else if (freq === "monthly") {
          const weeks = w.record.week_numbers;
          if (weeks && weeks.length > 0) {
            lastDate = lastDateMonthlyNthWeek(w, limit);
          } else {
            lastDate = lastDateMonthlyDateOfMonth(w, limit);
          }
        } else if (freq === "yearly") {
          lastDate = lastDateYearly(w, limit);
        }

        if (lastDate && (!bestDate || cmpDate(lastDate, bestDate) > 0)) {
          bestDate = lastDate;
          bestRecord = w.record;
        }
      }

      if (bestDate) {
        const endTime = (bestRecord?.end_time ?? history.to_time ?? "23:59:59");
        const startTime = (bestRecord?.start_time ?? history.from_time ?? "00:00:00");
        baseLastEnd = `${bestDate} ${endTime}`;
        baseLastStart = `${bestDate} ${startTime}`;
      } else {
        // If every window is unbounded (or no date found), leave baseLastEnd null for now.
      }
    }

    // ----- Apply modifications (only those that belong to this history) -----
    let latestFromMods: string | null = null; // max new_end
    let cancelledLast = false;

    if (history.modifications && history.modifications.length > 0) {
      // latest new_end among non-cancelled mods
      for (const mod of history.modifications) {
        if (!mod) continue;
        // Cancellation? Note: only matters if it cancels the computed base last
        if (mod.is_cancelled && baseLastStart && mod.old_start === baseLastStart) {
          cancelledLast = true;
        }
        if (!mod.is_cancelled && mod.new_end) {
          if (!latestFromMods || mod.new_end > latestFromMods) latestFromMods = mod.new_end;
        }
      }
    }

    // If our base last occurrence was cancelled and there is a bounded recurrence, step to previous one
    const stepBackOne = (start: string | null) => {
      if (!start || !recs || recs.length === 0) return null;
      const windows = buildActiveWindows(recs as any);
      // find the window containing base date
      const dateOnly = start.slice(0, 10);
      const freqRec = windows.find(w =>
        cmpDate(dateOnly, w.from) >= 0 && (w.to ? cmpDate(dateOnly, w.to) <= 0 : true)
      );
      if (!freqRec) return null;
      const freq = (freqRec.record.recurring_frequency || "").toLowerCase();
      const limit = addDays(dateOnly, -1);
      let prev: string | null = null;

      if (freq === "daily") prev = lastDateDaily(freqRec, limit);
      else if (freq === "weekly") prev = lastDateWeekly(freqRec, limit);
      else if (freq === "monthly") {
        const weeks = freqRec.record.week_numbers;
        prev = (weeks && weeks.length > 0)
          ? lastDateMonthlyNthWeek(freqRec, limit)
          : lastDateMonthlyDateOfMonth(freqRec, limit);
      } else if (freq === "yearly") prev = lastDateYearly(freqRec, limit);

      if (!prev) return null;
      const endTime = (freqRec.record.end_time ?? history.to_time ?? "23:59:59");
      return `${prev} ${endTime}`;
    };

    // Decide final
    let candidate = baseLastEnd || "";
    if (cancelledLast) {
      const prev = stepBackOne(baseLastStart);
      candidate = prev || "";
    }
    if (latestFromMods && (!candidate || latestFromMods > candidate)) {
      candidate = latestFromMods;
    }

    // If still empty (e.g., fully unbounded recurring without mods), return empty string.
    return candidate;
  }

  // Emit a reservation for a date
  const emit = (date: string, h: Reservation, r?: RecurringRecord) => {
    const isRecurring = h.recurring ? h.recurring.length > 0 : false;

    const lastOccurrence = lastOccurrenceString(h);
    //console.log(`lastOccurrence for history id=${h.id} is '${lastOccurrence}'`);
    const isReturned = !h.isBorrowing;
    const isReturnPast = moment().isAfter(moment(lastOccurrence, "YYYY-MM-DD HH:mm:ss"));
    const isLate = !isReturned && isReturnPast;
    const isCancelled = r?.is_cancelled ?? false;

    const from_time = r?.start_time ?? h.from_time;
    const to_time   = r?.end_time   ?? h.to_time;
    const comment   = r?.comment    ?? h.comment;

    results.push({
      parentId: h.id,
      borrowerName: `${h.user?.last_name ?? ""}, ${h.user?.first_name ?? ""}`,
      date: date,
      ...(from_time ? { fromTime: from_time } : {}),
      ...(to_time ? { toTime: to_time } : {}),
      title,
      comment: `${isRecurring ? "⟳ " : ""}${comment ?? "補足メモなし"}`,
      vehicleId: vehicleId,
      isReturned: isReturned,
      isLate: isLate,
      isRecurring: isRecurring,
      isCancelled: isCancelled,
    });
  };

  // Generate occurrences for DAILY record within [activeFrom, activeTo]
  const generateDaily = (
    record: RecurringRecord,
    activeFrom: string,
    activeTo: string,
    history: Reservation
  ) => {
    const gap = Math.max(1, record.recurring_gap ?? 1);
    // Determine the first occurrence >= activeFrom based on start_date
    const start = parseDateStrict(record.start_date, history);
    if (!start) return;
    if (compareDate(start, activeTo) > 0) return;

    // Find k so that start + k*gap >= activeFrom
    const deltaDays = Math.max(0, Math.ceil((stringToDate(activeFrom).getTime() - stringToDate(start).getTime()) / (1000 * 3600 * 24)));
    const startK = Math.max(0, Math.floor((deltaDays + (gap - 1)) / gap)); // ceil(deltaDays/gap)
    for (let k = startK; ; k++) {
      const occ = addDays(start, k * gap);
      if (compareDate(occ, activeTo) > 0) break;
      if (compareDate(occ, activeFrom) < 0) continue;
      emit(occ, history, record);
    }
  };

  // Generate occurrences for WEEKLY record within [activeFrom, activeTo]
  const generateWeekly = (record: RecurringRecord, activeFrom: string, activeTo: string, history: Reservation) => {
    const gapWeeks = Math.max(1, record.recurring_gap ?? 1);
    const days = normalizeDowList(record.days_of_week);
    if (days.length === 0) return;

    const start = parseDateStrict(record.start_date, history);
    if (!start) return;

    const startDate = stringToDate(start);
    const startDayOfWeek = startDate.getDay(); // 0..6 (0=Sunday)

    const msPerDay = 24 * 3600 * 1000;
    const stepDays = gapWeeks * 7;

    // For each requested weekday, build its own progression
    for (const desiredDow of days) {
      // First occurrence inside the first 7-day span starting at start_date
      const seedOffset =
        desiredDow >= startDayOfWeek
          ? (desiredDow - startDayOfWeek)                   // same week
          : (7 - (startDayOfWeek - desiredDow));            // push to next week

      let occ = addDays(start, seedOffset); // as "YYYY-MM-DD"

      // If the active window starts later, jump forward by whole stepDays
      const diffDaysFromActiveFrom = Math.ceil(
        (stringToDate(activeFrom).getTime() - stringToDate(occ).getTime()) / msPerDay
      );
      if (diffDaysFromActiveFrom > 0) {
        const steps = Math.floor((diffDaysFromActiveFrom + (stepDays - 1)) / stepDays); // This line does int division of the following: ceil / stepDays
        if (steps > 0) {
          occ = addDays(occ, steps * stepDays);
        }
      }

      // Emit occurrences while inside [activeFrom, activeTo]
      while (compareDate(occ, activeTo) <= 0) {
        if (compareDate(occ, activeFrom) >= 0) {
          emit(occ, history, record);
        }
        occ = addDays(occ, stepDays);
      }
    }
  };

  // Generate occurrences for MONTHLY record within [activeFrom, activeTo]
  const generateMonthly = (
    record: RecurringRecord,
    activeFrom: string,
    activeTo: string,
    history: Reservation
  ) => {
    const gapMonths = Math.max(1, record.recurring_gap ?? 1);
    const days = normalizeDowList(record.days_of_week); // [] if falsy or empty
    const weeksRaw = record.week_numbers ?? null;        // null/undefined => date-of-month mode
    // console.log(`activeFrom=${activeFrom}, activeTo=${activeTo}, gapMonths=${gapMonths}, days=${days}, weeksRaw=${weeksRaw}`);
    // console.log("record");
    // console.log(record);
    // console.log("history");
    // console.log(history);

    const startDate = parseDateStrict(record.start_date, history);
    if (!startDate) return;

    // ---- small local helpers (string-date based) ----
    const monthIndex = (s: string) => {
      const { y, m } = stringToNumbers(s);
      return y * 12 + (m - 1);
    };
    const firstOfMonth = (s: string): string => {
      const { y, m } = stringToNumbers(s);
      return dateToString(numbersToDate(y, m, 1));
    };
    const lastOfMonth = (s: string): string => {
      const { y, m } = stringToNumbers(s);
      // last day: day 0 of next month
      const last = new Date(numbersToDate(y, m, 1).getFullYear(), numbersToDate(y, m, 1).getMonth() + 1, 0);
      return dateToString(last);
    };
    const buildYMDIfValid = (y: number, m: number, d: number): string | null => {
      const dt = new Date(y, m - 1, d);
      if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) {
        return dateToString(dt);
      }
      return null; // invalid day for that month
    };
    const getDay = (s: string) => stringToDate(s).getDay(); // 0..6 (0=Sun)

    // "First 7-day span" mapping (carry earlier weekdays into following week)
    const firstOccInsideSpan = (spanStart: string, desiredDow: number): string => {
      const baseDow = getDay(spanStart);
      const offset = desiredDow >= baseDow
        ? (desiredDow - baseDow)
        : (7 - (baseDow - desiredDow));
      return addDays(spanStart, offset);
    };

    // iterate months on gap cadence, starting from start_date's month,
    // but skip forward to the first month that intersects activeFrom.
    const sMonthIdx = monthIndex(startDate);
    const activeFromMonthIdx = monthIndex(activeFrom);
    const activeToMonthIdx = monthIndex(activeTo);

    const startSteps =
      activeFromMonthIdx > sMonthIdx
        ? Math.ceil((activeFromMonthIdx - sMonthIdx) / gapMonths)
        : 0;

    // console.log(`sMonthIdx=${sMonthIdx}, activeFromMonthIdx=${activeFromMonthIdx}, activeToMonthIdx=${activeToMonthIdx}, startSteps=${startSteps}`);
    for (let step = startSteps; ; step++) {
      const currentMonthIdx = sMonthIdx + step * gapMonths;
      if (currentMonthIdx > activeToMonthIdx) break;

      // Compose first & last day of this month
      const y = Math.floor(currentMonthIdx / 12);
      const m = (currentMonthIdx % 12) + 1;
      const monthFirst = dateToString(numbersToDate(y, m, 1));
      const monthLast = lastOfMonth(monthFirst);

      // Skip whole month if its entire span is outside the active window
      if (compareDate(monthLast, activeFrom) < 0) continue;
      if (compareDate(monthFirst, activeTo) > 0) break;

      if (!weeksRaw || days.length === 0) {
        // ---- Date-of-month mode ----
        const day = stringToNumbers(startDate).d; // day-of-month from start_date
        const candidate = buildYMDIfValid(y, m, day);
        if (!candidate) continue; // day doesn't exist in this month (e.g., 31st)

        // Enforce not before start_date, and clamp to window
        if (compareDate(candidate, startDate) < 0) continue;
        if (compareDate(candidate, activeFrom) < 0) continue;
        if (compareDate(candidate, activeTo) > 0) break;

        emit(candidate, history, record);
        continue;
      }

      // ---- Week-number + weekday mode ----
      // Normalize week_numbers to allowed set {1,2,3,4,-1}
      const weeks = Array.from(
        new Set(
          toNumberArray(weeksRaw)
            .filter(n => n === -1 || (n >= 1 && n <= 4))
            .map(n => n)
        )
      ).sort((a, b) => (a === -1 ? 1 : b === -1 ? -1 : a - b)); // put -1 last

      // Precompute successive 7-day spans starting at the 1st
      const spans: string[] = []; // list of span starts (week 1 start, week 2 start, ...)
      let spanStart = monthFirst; // week 1 starts at the 1st (partial week allowed)
      while (compareDate(spanStart, monthLast) <= 0) {
        spans.push(spanStart);
        spanStart = addDays(spanStart, 7);
      }
      const lastSpanStart = spans[spans.length - 1];

      for (const wk of weeks) {
        const baseStart =
          wk === -1
            ? lastSpanStart
            : (wk - 1) < spans.length ? spans[wk - 1] : null;
        if (!baseStart) continue;

        for (const desiredDow of days) {
          const occ = firstOccInsideSpan(baseStart, desiredDow);

          // Must fall inside this month
          if (stringToNumbers(occ).m !== m || stringToNumbers(occ).y !== y) continue;

          // Enforce not before start_date, and within window
          if (compareDate(occ, startDate) < 0) continue;
          if (compareDate(occ, activeFrom) < 0) continue;
          if (compareDate(occ, activeTo) > 0) continue;

          emit(occ, history, record);
        }
      }
    }
  };

  // Generate occurrences for YEARLY record within [activeFrom, activeTo]
  const generateYearly = (
    record: RecurringRecord,
    activeFrom: string,
    activeTo: string,
    history: Reservation
  ) => {
    const gapYears = Math.max(1, record.recurring_gap ?? 1);
    const days = normalizeDowList(record.days_of_week); // [] if falsy or empty
    const weeksRaw = record.week_numbers ?? null;        // null/undefined => date-of-month mode

    const s = parseDateStrict(record.start_date, history);
    if (!s) return;

    // ---- local helpers (string-date based) ----
    const ymd = (s: string) => {
      const [y, m, d] = s.split("-").map(Number);
      return { y, m, d };
    };
    const yearIndex = (s: string) => ymd(s).y;

    const numbersToDateSafe = (y:number,m:number,d:number) => new Date(y, m-1, d);
    const dateToStringLocal = (date: Date): string => {
      const y = date.getFullYear();
      const m = String(date.getMonth()+1).padStart(2,"0");
      const d = String(date.getDate()).padStart(2,"0");
      return `${y}-${m}-${d}`;
    };

    const firstOfMonth = (y:number, m:number): string =>
      dateToStringLocal(numbersToDateSafe(y, m, 1));

    const lastOfMonth = (y:number, m:number): string => {
      const last = new Date(y, m, 0); // day 0 of next month -> last day of (y,m)
      return dateToStringLocal(last);
    };

    const buildYMDIfValid = (y: number, m: number, d: number): string | null => {
      const dt = new Date(y, m - 1, d);
      if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) {
        return dateToStringLocal(dt);
      }
      return null; // invalid (e.g., Feb 29 on non-leap)
    };

    const getDay = (s: string) => stringToDate(s).getDay(); // 0..6 (0=Sun)

    // Map desired weekday into the first 7-day span starting at spanStart
    const firstOccInsideSpan = (spanStart: string, desiredDow: number): string => {
      const baseDow = getDay(spanStart);
      const offset = desiredDow >= baseDow
        ? (desiredDow - baseDow)
        : (7 - (baseDow - desiredDow));
      return addDays(spanStart, offset);
    };

    const { y: sy, m: sm, d: sd } = ymd(s);

    // Find earliest year-step whose target month could intersect the active window.
    const sYearIdx = yearIndex(s);
    const activeFromYearIdx = yearIndex(activeFrom);
    const activeToYearIdx = yearIndex(activeTo);

    const startSteps =
      activeFromYearIdx > sYearIdx
        ? Math.ceil((activeFromYearIdx - sYearIdx) / gapYears)
        : 0;

    for (let step = startSteps; ; step++) {
      const y = sy + step * gapYears;
      if (y > activeToYearIdx + 1) break; // cheap guard

      const monthFirst = firstOfMonth(y, sm);
      const monthLast = lastOfMonth(y, sm);

      // Skip if this year's target month is entirely before window; stop if entirely after
      if (compareDate(monthLast, activeFrom) < 0) continue;
      if (compareDate(monthFirst, activeTo) > 0) break;

      if (!weeksRaw || days.length === 0) {
        // ---- Date-of-month mode ----
        const candidate = buildYMDIfValid(y, sm, sd);
        if (!candidate) continue;                       // invalid date for this year (e.g., Feb 29 non-leap)
        if (compareDate(candidate, s) < 0) continue;    // don't emit before start_date
        if (compareDate(candidate, activeFrom) < 0) continue;
        if (compareDate(candidate, activeTo) > 0) break;

        emit(candidate, history, record);
        continue;
      }

      // ---- Week-number + weekday mode ----
      // Normalize week_numbers to {1,2,3,4,-1}
      const weeks = Array.from(
        new Set(
          toNumberArray(weeksRaw)
            .filter(n => n === -1 || (n >= 1 && n <= 4))
            .map(n => n)
        )
      ).sort((a, b) => (a === -1 ? 1 : b === -1 ? -1 : a - b)); // -1 last

      // Build span starts: week 1 starts on the 1st (partial week allowed), then +7 days, etc.
      const spans: string[] = [];
      let spanStart = monthFirst;
      while (compareDate(spanStart, monthLast) <= 0) {
        spans.push(spanStart);
        spanStart = addDays(spanStart, 7);
      }
      const lastSpanStart = spans[spans.length - 1];

      for (const wk of weeks) {
        const baseStart =
          wk === -1
            ? lastSpanStart
            : (wk - 1) < spans.length ? spans[wk - 1] : null;
        if (!baseStart) continue;

        for (const desiredDow of days) {
          const occ = firstOccInsideSpan(baseStart, desiredDow);

          // Must fall in this month (y, sm)
          const { y: oy, m: om } = ymd(occ);
          if (oy !== y || om !== sm) continue;

          // Not before start_date; inside active window
          if (compareDate(occ, s) < 0) continue;
          if (compareDate(occ, activeFrom) < 0) continue;
          if (compareDate(occ, activeTo) > 0) continue;

          emit(occ, history, record);
        }
      }
    }
  };

  function applyModifications(
    input: VehicleReservationEvent[],
    data: VehicleData
  ): VehicleReservationEvent[] {
    //console.log(`Applying modifications to ${input.length} occurrences`);
    const splitDateTime = (dt: string) => {
      if (!dt) return { date: "", time: "" };
      const [date, time] = dt.split(" "); // "YYYY-MM-DD HH:MM:SS"
      return { date, time };
    };

    // Build a fast lookup of current occurrences by (parentId, old_start)
    // We only match occurrences that have a fromTime (time-based reservations).
    const key = (parentId: number|null, date: string, time: string) =>
      `${parentId ?? "null"}|${date} ${time}`;

    // We’ll mutate a copy
    let out = [...input];

    const inWindow = (date: string) => (date >= windowStart && date <= windowEnd);
    const title = data.title;
    const vehicleId = data.id;

    // Process modifications per original reservation (history)
    for (const h of data.history ?? []) {
      if (!h.modifications || h.modifications.length === 0) continue;

      for (const mod of h.modifications) {
        if (mod.borrowings_id !== h.id) continue;

        const old = splitDateTime(mod.old_start);
        const newStart = splitDateTime(mod.new_start);
        const newEnd = splitDateTime(mod.new_end);

        // Remove the old occurrence(s) by matching parentId + date + fromTime
        out = out.filter(
          (ev) => !(ev.parentId === h.id && ev.date === old.date && ev.fromTime === old.time)
        );

        // If it's a pure cancellation (is_cancelled && no new_start), don't add anything back
        if (!mod.new_start || !mod.new_end) {
          continue;
        }

        if (inWindow(newStart.date)) {
          const lastOccurrence = lastOccurrenceString(h);
          const isReturned = !h.isBorrowing;
          const isReturnPast = moment().isAfter(
            moment(lastOccurrence, "YYYY-MM-DD HH:mm:ss")
          );
          const isLate = !isReturned && isReturnPast;

          const borrowerName = `${h.user?.last_name ?? ""}, ${
            h.user?.first_name ?? ""
          }`;

          out.push({
            parentId: h.id,
            borrowerName,
            date: newStart.date,
            fromTime: newStart.time,
            toTime: newEnd.time,
            title,
            comment: `${h.comment ?? "補足メモなし"}`,
            vehicleId,
            isReturned,
            isLate,
            isRecurring: false,
            isCancelled: !!mod.is_cancelled,
            modificationId: mod.id,
            modificationConstructionId: mod.construction_id ?? null,
            modificationComment: mod.comment ?? null,
          });
        }
      }
    }

    return out;
  }

  // Parse recurringRecord.start_date; if missing, fallback to history.from (only for recurring records)
  function parseDateStrict(maybe: string | undefined, h: Reservation): string | null {
    const val = maybe ?? h.from;
    if (!val) return null;
    try {
      return dateToString(stringToDate(val)); // validate format
    } catch {
      return null;
    }
  }

  // Build non-overlapping active windows for recurring records (supersession logic).
  // Sort records by start_date asc; each record is active from its start_date until min(its end_date, day-before next record's start_date).
  const buildActiveWindows = (recs: RecurringRecord[], h: Reservation) => {
    const withStarts = recs
      //.filter(r => r && !r.is_cancelled)
      .filter(r => r) // remove null/undefined, no longer filtering cancelled here
      .map(r => ({
        ...r,
        _start: parseDateStrict(r.start_date, h), // fallback to history.from if necessary
        _end: r.end_date ? r.end_date : null,
      }))
      .filter(r => r._start); // must have a start
    withStarts.sort((a, b) => compareDate(a._start!, b._start!));

    const windows: Array<{ record: RecurringRecord; from: string; to: string | null }> = [];
    for (let i = 0; i < withStarts.length; i++) {
      const cur = withStarts[i];
      const next = withStarts[i + 1];
      const hardEnd = cur._end; // may be null (no end)
      let to: string | null = null;

      if (next) {
        // Ends the day before the next record starts, unless the record's own end is earlier
        const dayBeforeNext = addDays(next._start!, -1);
        to = hardEnd ? minDate(hardEnd, dayBeforeNext) : dayBeforeNext;
      } else {
        // No later record. End at own end if given; otherwise unbounded (null)
        to = hardEnd ?? null;
      }
      windows.push({ record: cur as RecurringRecord, from: cur._start!, to });
    }
    return windows;
  };

  // Main per-history processing
  for (const h of relevantHistory) {
    const lastOccurrence = lastOccurrenceString(h);
    //console.log(`lastOccurrence for history id=${h.id} is '${lastOccurrence}'`);
    const isReturned = !h.isBorrowing;
    const isReturnPast = moment().isAfter(moment(lastOccurrence, "YYYY-MM-DD HH:mm:ss"));
    const isLate = !isReturned && isReturnPast;

    const recs = h.recurring ?? [];

    if (!recs || recs.length === 0) {
      // One-time reservation using from/to (inclusive)
      if (!h.from) continue;
      const fromD = h.from;
      const toD = h.to ? h.to : fromD;
      const clamped = clampRange(fromD, toD, windowStart, windowEnd);
      if (!clamped) continue;

      const isRecurring = false;
      results.push({
        parentId: h.id,
        borrowerName: `${h.user?.last_name ?? ""}, ${h.user?.first_name ?? ""}`,
        date: h.from,
        endDate: h.to,
        ...(h.from_time ? { fromTime: h.from_time } : {}),
        ...(h.to_time ? { toTime: h.to_time } : {}),
        title : title,
        comment: `${isRecurring ? "⟳ " : ""}${h.comment ?? "補足メモなし"}`,
        vehicleId: vehicleId,
        isReturned: !h.isBorrowing,
        isLate: isLate,
        isRecurring,
      });

      continue;
    }

    // Recurring: ignore h.from/h.to, use records with supersession
    const windows = buildActiveWindows(recs, h);
    for (const w of windows) {
      // Determine effective range intersected with global window
      const rangeFrom = w.from;
      const rangeTo = w.to ?? windowEnd; // unbounded => cap to windowEnd
      const clamped = clampRange(rangeFrom, rangeTo, windowStart, windowEnd);
      if (!clamped) continue;

      const freq = w.record.recurring_frequency;
      if (freq === "daily") {
        generateDaily(w.record, clamped.from, clamped.to, h);
      } else if (freq === "weekly") {
        generateWeekly(w.record, clamped.from, clamped.to, h);
      } else if (freq === "monthly") {
        generateMonthly(w.record, clamped.from, clamped.to, h);
      } else if (freq === "yearly") {
        generateYearly(w.record, clamped.from, clamped.to, h);
      } else {
        continue;
      }
    }
  }

  const resultsWithMods = applyModifications(results, data);

  resultsWithMods.sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    const at = a.fromTime ?? "00:00:00";
    const bt = b.fromTime ?? "00:00:00";
    return at.localeCompare(bt);
  });
  return resultsWithMods;
}